import axios from 'axios';

/**
 * Login Activity Service
 *
 * Handles API requests for login activity and User Location Map feature.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

/**
 * Map data point interface (matches backend)
 */
export interface MapDataPoint {
  id: string;
  user_id: string | null;
  user_name: string;
  user_email: string;
  company_id: string | null;
  company_name: string;
  latitude: number;
  longitude: number;
  city: string;
  region: string;
  country: string;
  country_code: string;
  login_timestamp: string;
  user_agent: string | null;
  success: boolean;
  failure_reason: string | null;
  attempted_email: string | null;
  event_type: string;
  referrer: string | null;
  page_url: string | null;
}

/**
 * Map statistics interface
 */
export interface MapStats {
  total_logins_with_location: number;
  unique_countries: number;
  unique_users: number;
  most_recent_login: string | null;
}

/**
 * Get map data for visualization
 * Returns login locations for the map
 * Platform Admin only
 * @param token - JWT authentication token
 * @param days - Optional filter for last N days (1, 7, 30, 90, or undefined for all time)
 * @param successFilter - Optional filter: true = successful only, false = failed only, undefined = all
 */
export async function getMapData(token: string, days?: number, successFilter?: boolean, eventTypeFilter?: string): Promise<MapDataPoint[]> {
  const params: any = {};
  if (days !== undefined) {
    params.days = days;
  }
  if (successFilter !== undefined) {
    params.success = successFilter.toString();
  }
  if (eventTypeFilter !== undefined) {
    params.eventType = eventTypeFilter;
  }

  const response = await axios.get(`${API_BASE_URL}/login-activity/map-data`, {
    headers: {
      Authorization: `Bearer ${token}`
    },
    params
  });
  return response.data.data;
}

/**
 * Get map statistics
 * Returns aggregate stats for the map dashboard
 * Platform Admin only
 * @param token - JWT authentication token
 * @param days - Optional filter for last N days (1, 7, 30, 90, or undefined for all time)
 * @param successFilter - Optional filter: true = successful only, false = failed only, undefined = all
 */
export async function getMapStats(token: string, days?: number, successFilter?: boolean, eventTypeFilter?: string): Promise<MapStats> {
  const params: any = {};
  if (days !== undefined) {
    params.days = days;
  }
  if (successFilter !== undefined) {
    params.success = successFilter.toString();
  }
  if (eventTypeFilter !== undefined) {
    params.eventType = eventTypeFilter;
  }

  const response = await axios.get(`${API_BASE_URL}/login-activity/map-stats`, {
    headers: {
      Authorization: `Bearer ${token}`
    },
    params
  });
  return response.data.data;
}

/**
 * Track an anonymous page visit (public, no auth required)
 * Fire-and-forget - errors are silently swallowed
 */
export async function trackPageVisit(data: {
  referrer?: string;
  page_url?: string;
  screen_resolution?: string;
  language?: string;
}): Promise<void> {
  try {
    await axios.post(`${API_BASE_URL}/track/page-visit`, data);
  } catch {
    // Silently swallow - analytics should never break UX
  }
}

const loginActivityService = {
  getMapData,
  getMapStats,
  trackPageVisit
};

export default loginActivityService;
